import type { ApiEnvelope } from '@/api/errors';
import type {
  MyBestOffer,
  MyOffersSide,
  SellPriceCandidatesByPrepayment,
  SellPortCandidate,
  TransportSurcharges,
} from '@/types/bestPrice';
import type { BuyCptBestPrice, CptPricePerTonForPurchaseOrder } from '@/types/buy';
import type { SellBestPrice } from '@/types/sell';
import { BaseService } from './BaseService';

/** Параметры ориентира цены продажи: достаточно базиса, культуры и качества (год эндпоинтом игнорируется). */
export interface RecommendedSellParams {
  /** Адрес моего базиса (точка погрузки). */
  readonly address: string;
  /** Культура с классом («Пшеница 4 класс»). */
  readonly goodsType: string;
  /** Вариант качества (propsSetId) — единственное обязательное поле эндпоинта. */
  readonly propertySetId: number;
}

/** Сырой порт-кандидат ориентира: price приходит строкой (@JsonFormat STRING), distance — км. */
interface SellPortCandidateDto {
  readonly purchaseOrderId?: number;
  readonly purchaseOrderVersion?: number;
  readonly buyerAddressName?: string;
  readonly price?: string | number;
  readonly distance?: number;
}

/** Сырой ответ ставок надбавок (GET /sell/transport-surcharges). */
interface TransportSurchargesDto {
  readonly noScalesRate?: string | number;
  readonly noTralRate?: string | number;
  readonly noDumpTruckRate?: string | number;
  readonly minDistanceKm?: number;
}

export interface CptForPriceParams {
  readonly purchaseOrderIds: number[];
  readonly address: string;
  readonly goodsTypeParent: string;
  /** Год урожая — только если сервер прислал его в группе; best-price его не учитывает (решение 2026-11-04). */
  readonly cropYear?: number;
}

export interface SellForAddressParams {
  /** Адрес моего базиса (точка погрузки) — откуда продаю. */
  readonly address: string;
  /** Культура с классом («Пшеница 3 класс»). */
  readonly goodsType: string;
  /** Вариант качества (propsSetId из словаря /order/goods_propersties). */
  readonly propertySetId: number;
  /** PERCENT_0 / PERCENT_100; пустой список бэкенд трактует как «оба». */
  readonly prepaymentTypes: string[];
  readonly limit: number;
  readonly offset: number;
}

export interface CptForAddressParams {
  /** Адрес выгрузки покупателя (строка из автокомплита). */
  readonly address: string;
  /** Культура с классом («Пшеница 3 класс»). */
  readonly goodsType: string;
  /** Вариант качества (propsSetId из словаря /order/goods_propersties). */
  readonly propertySetId: number;
  /** PERCENT_0 / PERCENT_100; пустой список бэкенд трактует как «оба». */
  readonly prepaymentTypes: string[];
  readonly limit: number;
  readonly offset: number;
}

// Сервис best-price-service-fast: лучшие предложения продажи под мои закупки.
// Ошибки best-price — мягкие (UI не блокирует грид), поэтому метод не бросает на пустоту,
// а транспортные ошибки обрабатывает вызывающий хук (трактует как «рынок недоступен»).
class BestPriceService extends BaseService {
  /** Лучшая цена продажи по моим строкам закупки (для сравнения в гриде). */
  async getCptForPrice(
    params: CptForPriceParams,
    signal?: AbortSignal,
  ): Promise<CptPricePerTonForPurchaseOrder[]> {
    const response = await this.request<ApiEnvelope<CptPricePerTonForPurchaseOrder[]>>(
      { method: 'post', url: '/best/price/v2/page/buy/fca_teo/for-price', data: params },
      // Read-only POST (lookup) — идемпотентен, транзиентные сбои безопасно повторять (плейбук).
      { idempotent: true, signal },
    );
    return response.data?.data ?? [];
  }

  /**
   * Поиск лучших предложений продажи под произвольную закупку (экран «Лучшая цена»).
   * Бэкенд возвращает список, отсортированный по CPT-цене (первый — лучший);
   * price сериализуется строкой (@JsonFormat STRING) — нормализуем в число.
   * Живой dev отдаёт в data голый массив; в коде репо — обёртку { bestCptPricePerTonList } —
   * принимаем обе формы (сверено вживую 11.06.2026).
   */
  async getCptForAddress(params: CptForAddressParams, signal?: AbortSignal): Promise<BuyCptBestPrice[]> {
    const response = await this.request<
      ApiEnvelope<BuyCptBestPrice[] | { bestCptPricePerTonList?: BuyCptBestPrice[] }>
    >(
      { method: 'post', url: '/best/price/v2/page/buy/fca_teo/for-address', data: params },
      // Read-only POST (lookup) — идемпотентен, транзиентные сбои безопасно повторять (плейбук).
      { idempotent: true, signal },
    );
    const data = response.data?.data;
    const items = Array.isArray(data) ? data : (data?.bestCptPricePerTonList ?? []);
    return items.map((item) => ({ ...item, price: Number(item.price) }));
  }

  /**
   * Поиск лучших цен ПОКУПКИ под мой товар на базисе (экран «Лучшая цена» раздела «Продам»).
   * Зеркало getCptForAddress, но возвращает предложения покупателей (buyer*, pricePerTon).
   * pricePerTon сериализуется строкой (@JsonFormat STRING) — нормализуем в число; data — голый
   * массив (как у buy/for-address на живом dev).
   */
  async getSellForAddress(params: SellForAddressParams, signal?: AbortSignal): Promise<SellBestPrice[]> {
    const response = await this.request<ApiEnvelope<SellBestPrice[]>>(
      { method: 'post', url: '/best/price/v2/page/sell/for-address', data: params },
      // Read-only POST (lookup) — идемпотентен, транзиентные сбои безопасно повторять (плейбук).
      { idempotent: true, signal },
    );
    const items = response.data?.data ?? [];
    return items.map((item) => ({ ...item, pricePerTon: Number(item.pricePerTon) }));
  }

  /**
   * Ориентир «по какой цене можно продать»: кандидаты по портам с разбивкой по типам оплаты —
   * POST /best/price/v2/page/sell/best-in-ports/with-prepayments. Эндпоинту достаточно базиса+культуры+
   * качества; cropYear он игнорирует (не передаём). Ответ — карта тип-оплаты → список портов-кандидатов
   * с FCA при ВСЁМ включённом оборудовании (без надбавок) и дистанцией. Клиент сам вычитает надбавки при
   * отключении оборудования и берёт максимум по портам (мгновенно, без повторного запроса). price строкой →
   * нормализуем; кандидатов без реальной цены отбрасываем. Мягкий: пустота не ошибка.
   */
  async getSellPriceCandidates(params: RecommendedSellParams, signal?: AbortSignal): Promise<SellPriceCandidatesByPrepayment> {
    const response = await this.request<ApiEnvelope<Record<string, SellPortCandidateDto[]>>>(
      {
        method: 'post',
        url: '/best/price/v2/page/sell/best-in-ports/with-prepayments',
        data: { address: params.address, goodsType: params.goodsType, propertySetId: params.propertySetId },
      },
      // Read-only POST (lookup) — идемпотентен, транзиентные сбои безопасно повторять (плейбук).
      { idempotent: true, signal },
    );
    const map = response.data?.data ?? {};
    const result: SellPriceCandidatesByPrepayment = {};
    for (const [prepaymentType, list] of Object.entries(map)) {
      result[prepaymentType] = (list ?? [])
        .map(
          (dto): SellPortCandidate => ({
            purchaseOrderId: dto?.purchaseOrderId,
            purchaseOrderVersion: dto?.purchaseOrderVersion,
            buyerAddressName: dto?.buyerAddressName,
            price: Number(dto?.price),
            distance: dto?.distance,
          }),
        )
        .filter((candidate) => Number.isFinite(candidate.price) && candidate.price > 0);
    }
    return result;
  }

  /**
   * Ставки надбавок к тарифу перевозки (₽/т·км) за отсутствие весов/трала/самосвала и порог дистанции —
   * GET /best/price/v2/page/sell/transport-surcharges (источник config-server). Для подписей у переключателей
   * оборудования и клиентского пересчёта ориентира. Значения приходят числом/строкой → нормализуем.
   */
  async getTransportSurcharges(signal?: AbortSignal): Promise<TransportSurcharges> {
    const response = await this.request<ApiEnvelope<TransportSurchargesDto>>(
      { method: 'get', url: '/best/price/v2/page/sell/transport-surcharges' },
      { idempotent: true, signal },
    );
    const dto = response.data?.data ?? {};
    return {
      noScalesRate: Number(dto.noScalesRate) || 0,
      noTralRate: Number(dto.noTralRate) || 0,
      noDumpTruckRate: Number(dto.noDumpTruckRate) || 0,
      minDistanceKm: Number(dto.minDistanceKm) || 0,
    };
  }

  /**
   * Упреждающий прогрев кэша расстояний продавец→порты для адреса погрузки. Зовётся при ВЫБОРЕ адреса
   * в форме продажи — ДО запроса ориентира, чтобы к его моменту кэш был тёплым и ориентир пришёл мгновенно.
   * Fire-and-forget: не ждём результата и глотаем ошибки — это лишь оптимизация задержки, корректность
   * ориентира гарантирует его самолечение на бэке. Сервер отвечает 202 и считает расстояния асинхронно.
   */
  async prewarmSellDistances(address: string): Promise<void> {
    try {
      await this.request<ApiEnvelope>(
        { method: 'post', url: '/best/price/v2/page/sell/prewarm-distances', data: { address } },
        // Не ретраим необязательную оптимизацию; сам прогрев на бэке идемпотентен.
        { retry: false },
      );
    } catch {
      // Прогрев необязателен — ошибку намеренно игнорируем (ориентир самолечится).
    }
  }

  /**
   * Топ лучших встречных предложений по ВСЕМ моим позициям (виджет дашборда).
   * Один серверный агрегат вместо десятков per-order запросов. Цены приходят строкой
   * (@JsonFormat STRING) — нормализуем в number. Мягкий: транзиентные сбои обрабатывает хук.
   */
  async getMyBestOffers(side: MyOffersSide = 'ALL', limit = 10, signal?: AbortSignal): Promise<MyBestOffer[]> {
    const response = await this.request<ApiEnvelope<MyBestOfferDto[]>>(
      { method: 'get', url: '/best/price/v2/page/my/best-offers', params: { side, limit }, signal },
      { idempotent: true, signal },
    );
    return (response.data?.data ?? []).map(normalizeOffer);
  }
}

// Сырой DTO: числовые поля приходят строкой (@JsonFormat STRING на BigDecimal).
type MyBestOfferDto = Omit<MyBestOffer, 'myPrice' | 'bestPrice' | 'advantage'> & {
  readonly myPrice?: string | number;
  readonly bestPrice?: string | number;
  readonly advantage?: string | number;
};

function toNumber(value: string | number | undefined): number | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  const num = Number(value);
  return Number.isNaN(num) ? undefined : num;
}

function normalizeOffer(dto: MyBestOfferDto): MyBestOffer {
  return {
    ...dto,
    myPrice: toNumber(dto.myPrice),
    bestPrice: toNumber(dto.bestPrice),
    advantage: toNumber(dto.advantage),
  };
}

export const bestPriceService = new BestPriceService();
